import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { generateBlackSquare } from "@/utils/storage";
import { useCollectionMetadata } from "@/hooks/useCollectionMetadata";
import { backend } from "@/services/backend";
import { useWallet } from "@/context/WalletContext";
import { useTonConnect } from "@/hooks/useTonConnect";
import { useCollectionContract } from "@/hooks/useCollectionContract";
import { useSnackbar } from "notistack";
import { ratingBackend } from "@/services/ratingBackend";
import {
  calcLevelByExperience,
  getExperienceProgress,
} from "@/utils/experience";

import {
  CollectionDataCache,
  CollectionStats,
  ItemInCollection,
  NftCard,
} from "@/types/collection";
import { WalletListItem } from "@/services/backend";
import { useSelector } from "react-redux";
import { RootState } from "@/state/reducer";
import { useWhitelistStatus } from "./useWhitelistStatus";

const INITIAL_DISPLAY_COUNT = 8;
const defaultNftImageUrl = generateBlackSquare(128, 128, false) as string;

export function useCollectionStats(
  collection?: string,
  collectionInfo?: CollectionDataCache
) {
  const [searchParams, setSearchParams] = useSearchParams();
  const snackbar = useSnackbar();
  const balance = useSelector((state: RootState) => state.balance);
  const { metadata, itemMetadata, loading } = useCollectionMetadata(
    collectionInfo?.data_url,
    collectionInfo?.item_data_url
  );

  const [modalStates, setModalStates] = useState({
    showWalletConnectionModal: false,
    showSuccessMessage: false,
    showErrorMessage: false,
    showStarVote: false,
  });

  const [loadingStates, setLoadingStates] = useState({
    isMinting: false,
    isLoadingItems: false,
  });

  const [previewImage, setPreviewImage] = useState<string>(defaultNftImageUrl);
  const [currentPage, setCurrentPage] = useState(0);
  const [items, setItems] = useState<ItemInCollection[]>([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [royaltyPercent, setRoyaltyPercent] = useState<number | null>(null);
  const [previousExperience, setPreviousExperience] = useState<number>(0);

  const { isConnected, walletAddress } = useWallet();
  const { fetchBalance } = useTonConnect();
  const { mint } = useCollectionContract();

  const [state, setState] = useState({
    displayCount: INITIAL_DISPLAY_COUNT,
    isLoading: false,
    isOwnerView: false,
  });

  const fetchItems = useCallback(
    async (page: number = 0, reset: boolean = false) => {
      if (!collection) return;

      setLoadingStates(prev => ({ ...prev, isLoadingItems: true }));
      try {
        const newItems = await backend.getItemsInCollection({
          collectionAddress: collection,
          page: page,
          perPage: INITIAL_DISPLAY_COUNT,
        });

        if (reset) {
          setItems(newItems);
          setCurrentPage(1);
        } else {
          setItems(prevItems => [...prevItems, ...newItems]);
          setCurrentPage(page + 1);
        }
      } catch (error) {
        console.error("Error loading items:", error);
      } finally {
        setLoadingStates(prev => ({ ...prev, isLoadingItems: false }));
      }
    },
    [collection]
  );

  const fetchRoyalty = useCallback(async () => {
    if (!collection) return;

    try {
      const royaltyParams =
        await backend.getCollectionRoyaltyParams(collection);
      const numerator = royaltyParams?.numerator ?? 0;
      const denominator = royaltyParams?.denominator ?? 1;

      const percentage = Math.round((numerator * 10000) / denominator) / 100;
      setRoyaltyPercent(percentage);
    } catch (error) {
      console.error("Error fetching royalty:", error);
    }
  }, [collection]);

  useEffect(() => {
    if (!collection) return;

    fetchRoyalty();
    fetchItems(0, true);
  }, [collection, fetchRoyalty, fetchItems]);

  useEffect(() => {
    if (isConnected) {
      setModalStates(prev => ({ ...prev, showWalletConnectionModal: false }));
    }
  }, [isConnected]);

  useEffect(() => {
    if (walletAddress && collectionInfo?.owner) {
      const isOwner = walletAddress === collectionInfo.owner;
      const viewFromParams = searchParams.get("view");

      let initialView = isOwner;

      if (viewFromParams && isOwner) {
        initialView = viewFromParams === "owner";
      }

      setState(prev => ({
        ...prev,
        isOwnerView: initialView,
      }));

      if (isOwner) {
        const newParams = new URLSearchParams(searchParams);
        newParams.set("view", initialView ? "owner" : "user");
        setSearchParams(newParams, { replace: true });
      }
    }
  }, [walletAddress, collectionInfo?.owner, searchParams, setSearchParams]);

  const stats = useMemo<CollectionStats>(
    () => ({
      name: metadata?.name || "",
      description: metadata?.description || "",
      background: metadata?.cover_image || "",
      image: metadata?.image || "",
      nft_image: itemMetadata?.image || "",
      nft_image_type: itemMetadata?.content_type,
      nft_image_content: itemMetadata?.content_url,
      minted_nfts: collectionInfo?.next_item_index || 0,
      contract_address: collectionInfo?.address || "",
      owner_address: collectionInfo?.owner || "",
      nextItemAddress: collectionInfo?.next_item_address || "",
      total: collectionInfo?.mint_limit || 0,
      price: (collectionInfo?.price || 0) / 1e9,
      earned:
        Math.round(
          (collectionInfo?.next_item_index || 0) *
            ((collectionInfo?.price || 0) / 1e9) *
            100
        ) / 100,
      available: Math.max(
        0,
        (collectionInfo?.mint_limit || 0) > 0
          ? (collectionInfo?.mint_limit || 0) -
              (collectionInfo?.next_item_index || 0)
          : 0
      ),
      mint_limit: collectionInfo?.mint_limit || 0,
      mint_time_limit: collectionInfo?.mint_time_limit
        ? collectionInfo.mint_time_limit
        : 0,
      is_sbt: collectionInfo?.is_sbt ?? true,
      enable_whitelist: collectionInfo?.enable_whitelist || false,
      user_item_limit: collectionInfo?.user_item_limit || 0,
    }),
    [metadata, itemMetadata, collectionInfo]
  );

  const { userProfile } = useWhitelistStatus({
    enabled: stats.enable_whitelist,
    wallet: walletAddress,
    collection: collection || "",
  });

  useEffect(() => {
    if (stats.nft_image !== previewImage) {
      setPreviewImage(stats.nft_image);
    }
  }, [stats.nft_image, previewImage]);

  const isMintingAllowed = useMemo(() => {
    // console.log("Collection  stats:", stats); // ToDo
    // console.log("Collection count: ", userProfile?.minted_count)
    if (stats.mint_limit && !stats.available) return false;
    if (stats.user_item_limit && stats.user_item_limit > 0) {
      if (userProfile && userProfile?.minted_count >= stats.user_item_limit) {
        return false;
      }
    }
    if (!stats.mint_time_limit) return true;
    
    const currentTime = Date.now();
    const mintDeadline = new Date(stats.mint_time_limit).getTime();

    return mintDeadline > currentTime;
  }, [stats.mint_limit, stats.available, stats.mint_time_limit, userProfile]);

  const nftCards = useMemo<NftCard[]>(() => {
    if (!items.length) return [];

    const baseImage = itemMetadata?.image || "";
    const baseName = metadata?.name || "";

    return items.map(item => ({
      id: `#${item.index}`,
      image: baseImage,
      name: `${baseName} #${item.index}`,
      mintedAddress: item.address,
    }));
  }, [items, itemMetadata?.image, metadata?.name]);

  const mintNft = useCallback(
    async (profileAddress: string | null) => {
      if (!collection) return;
      if (!isConnected) {
        snackbar.enqueueSnackbar("Please connect your wallet first", {
          SnackbarProps: {
            onClick: () => {
              snackbar.closeSnackbar();
            },
          },
          variant: "error",
          autoHideDuration: 1000,
        });
        setModalStates(prev => ({ ...prev, showWalletConnectionModal: true }));
        return;
      }

      try {
        // Fetch previous experience before minting
        if (walletAddress) {
          try {
            const ownerData =
              await ratingBackend.getOwnerRatingData(walletAddress);
            if (ownerData && ownerData.experience !== undefined) {
              setPreviousExperience(Number(ownerData.experience));
            }
          } catch (error) {
            console.error("Error fetching previous experience:", error);
            // Continue with minting even if previous experience fetch fails
          }
        }

        // const currentBalance = await fetchBalance();
        /* if (!currentBalance || Number(currentBalance) < stats.price) {
        snackbar.enqueueSnackbar("Insufficient balance", {
          variant: "error",
          autoHideDuration: 1000,
        });
        return;
      } */
        const result = await mint(collection, profileAddress ?? undefined);
        setLoadingStates(prev => ({ ...prev, isMinting: true }));

        if (result && collectionInfo?.next_item_address) {
          const MAX_ATTEMPTS = 24;
          let attempts = 0;
          while (attempts < MAX_ATTEMPTS) {
            const isDeployed = await backend.getDeployStatus(
              collectionInfo?.next_item_address
            );
            if (isDeployed) {
              setLoadingStates(prev => ({ ...prev, isMinting: false }));

              setModalStates(prev => ({ ...prev, showSuccessMessage: true }));
              return;
            }
            await new Promise(res => setTimeout(res, 5000));
            attempts++;
          }
          throw new Error("Deployment timeout after 2 minutes!");
        }
        throw new Error("Minting failed");
      } catch (error: any) {
        setLoadingStates(prev => ({ ...prev, isMinting: false }));
        setModalStates(prev => ({ ...prev, showErrorMessage: true }));
        setErrorMessage(error?.message || "Transaction error");
      }
    },
    [
      collection,
      isConnected,
      stats.price,
      mint,
      balance,
      collectionInfo?.next_item_address,
      snackbar,
      walletAddress,
    ]
  );

  const handleModalClose = useCallback(() => {
    setModalStates({
      showWalletConnectionModal: false,
      showSuccessMessage: false,
      showErrorMessage: false,
      showStarVote: false,
    });
  }, []);

  const handleLoadMore = useCallback(() => {
    fetchItems(currentPage, false);
  }, [fetchItems, currentPage]);

  const handleViewToggle = useCallback(() => {
    const newOwnerView = !state.isOwnerView;
    setState(prev => ({ ...prev, isOwnerView: newOwnerView }));

    if (
      walletAddress &&
      collectionInfo?.owner &&
      walletAddress === collectionInfo.owner
    ) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set("view", newOwnerView ? "owner" : "user");
      setSearchParams(newParams, { replace: true });
    }
  }, [
    state.isOwnerView,
    walletAddress,
    collectionInfo?.owner,
    searchParams,
    setSearchParams,
  ]);

  return {
    stats,
    metadata,
    itemMetadata,
    royaltyPercent,
    nftCards,
    items,
    loading,
    previewImage,
    isMinting: loadingStates.isMinting,
    isLoadingItems: loadingStates.isLoadingItems,
    isMintingAllowed,
    mintNft: (profileAddress: string | null) => mintNft(profileAddress),
    handleModalClose,
    handleLoadMore,
    handleViewToggle,
    state,
    showWalletConnectionModal: modalStates.showWalletConnectionModal,
    showSuccessMessage: modalStates.showSuccessMessage,
    showErrorMessage: modalStates.showErrorMessage,
    showStarVote: modalStates.showStarVote,
    errorMessage,
    previousExperience,
  };
}
